import {useEffect} from "react";
import {useQuery} from "@tanstack/react-query";
import {useLocation, useNavigate} from "react-router";
import {toast} from "react-toastify";
import keepSessionService from "../services/web/keep-session-service.js";
import {redirectToLogin} from "../services/http.js";
import useSession from "./use-session.js";
import {PermissionEnum} from "../utils/enums/permission-enum.js";
import {hasPermission} from "../utils/permission-matcher.js";

const POLL_INTERVAL = 60000;

export const MESSAGE_COUNT_QUERY_KEY = ["keep-session"];
export const NOTIFICATION_COUNT_QUERY_KEY = ["notifications-count"];

const SESSION_ERRORS = {
    "no.session": "Sessão encerrada",
    "logged.in.other.station": "Usuário logado em outra estação",
};

const resolveSessionError = ({response}) => {
    if (!response) return null;

    const data = response.data;
    const reason = typeof data === "string" ? data : data?.message;
    const known = SESSION_ERRORS[reason];

    if (known) return known;
    if (response.status === 401) return SESSION_ERRORS["no.session"];
    if (response.status === 403) return SESSION_ERRORS["logged.in.other.station"];

    return null;
};

const pollOptions = {
    refetchInterval: POLL_INTERVAL,
    refetchIntervalInBackground: true,
    retry: false,
};

const messageCountOptions = {
    ...pollOptions,
    queryKey: MESSAGE_COUNT_QUERY_KEY,
    queryFn: keepSessionService.keepSession,
};

export const useMessageCount = () => {
    const {data} = useQuery(messageCountOptions);
    return parseInt(data) || 0;
};

const useKeepSession = () => {
    const navigate = useNavigate();
    const {pathname} = useLocation();
    const {data: session} = useSession();

    const onNotificationsOpen = pathname.startsWith("/notifications-open");

    const {data: messageCount = 0, error: sessionError} = useQuery(messageCountOptions);

    const {data: notifications} = useQuery({
        ...pollOptions,
        queryKey: NOTIFICATION_COUNT_QUERY_KEY,
        queryFn: keepSessionService.notificationsCount,
        enabled: !onNotificationsOpen,
    });

    const {data: individualAlarm} = useQuery({
        ...pollOptions,
        queryKey: ["individual-alarm-has-events"],
        queryFn: keepSessionService.individualAlarmHasEvents,
        enabled: hasPermission(session?.permissions, PermissionEnum.INDIVIDUAL_ALARM),
    });

    useEffect(() => {
        if (!sessionError) return;

        const message = resolveSessionError(sessionError);
        if (message) redirectToLogin(message);
    }, [sessionError]);

    useEffect(() => {
        if (!notifications?.newEvent) return;

        toast.info("Há uma nova notificação em aberto", {
            toastId: "new-notification",
            onClick: () => navigate("/notifications-open"),
        });
    }, [notifications, navigate]);

    useEffect(() => {
        if (!individualAlarm?.hasNewEvents) return;

        toast.info("Há um novo alarme de indivíduo", {
            toastId: "new-individual-alarm",
            onClick: () => navigate(`/individual-alarm?lastAlarmId=${individualAlarm.lastAlarmId}`),
        });
    }, [individualAlarm, navigate]);

    return {
        messageCount: parseInt(messageCount) || 0,
        notificationCount: parseInt(notifications?.count) || 0,
    };
};

export default useKeepSession;
